import React, { useEffect, useState, useRef } from 'react';
import DownChevron from './icons/DownChevron';
import SearchIcon from './icons/Search';
import { Button, Menu, MenuItem, Fade } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

export interface ISearchField {
    id: string;
    displayName: string;
    inputType?: string;
}

export interface ISearchComponent {
    onChange: (value: React.ChangeEvent<HTMLInputElement>) => void;
    searchFields: ISearchField[];
    defaultSearchField?: ISearchField;
    placeholderOverride?: string;
    hasBorder?: boolean;
    value: string;
}

const SearchComponent = (props: ISearchComponent) => {
    const useStyles = makeStyles((theme: Theme) =>
        createStyles({
            root: {
                position: 'relative',
                fontSize: '1rem',
            },
            searchWrapper: {
                fontSize: 'inherit',
                background: '#FFFFFF',
                borderRadius: '0.5rem',
                height: '2.875rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.6875rem 0.9375rem',
                position: 'relative',
                outline: 'unset',
                border: props.hasBorder ? '1px solid #C1CAE1' : undefined,
                transition: '0.2s ease-in-out',
                boxSizing: 'border-box',
                '&:focus-within': {
                    background: '#FFFFFF',
                    border: props.hasBorder ? `1px solid ${theme.palette.primary.main}` : undefined,
                },
            },
            searchInput: {
                outline: 'none',
                background: 'none',
                border: 'none',
                flex: '1 1 auto',
                '&::placeholder': {
                    opacity: '0.75',
                },
            },
            optionDropdown: {
                fontSize: '0.8rem',
                flex: '0 0 auto',
                cursor: 'pointer',
            },
            optionsDropdownText: {
                marginRight: '0.3rem',
            },
            optionList: {
                borderRadius: '8px',
                width: '100%',
            },
            optionItem: {
                fontSize: '0.8rem',
                transition: '0.2s ease-in-out',
                margin: '0.25rem',
                padding: '0.25rem 0.9rem',
                cursor: 'pointer',
                borderRadius: '4px',
                '&:hover': {
                    background: '#E8EAF4',
                },
            },
        }),
    );

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedField, setSelectedField] = useState<ISearchField>();
    const [searchValue, setSearchValue] = useState<string>('');
    const [placeholder, setPlaceholder] = useState<string>();
    const inputRef = useRef(null);
    const anchorEl = useRef(null);
    const classes = useStyles();
    const { t } = useTranslation();

    useEffect(() => {
        setSearchValue(props.value);
    }, [props.value]);

    const onChange = (value: React.ChangeEvent<HTMLInputElement>) => {
        props.onChange(value);
        setSearchValue(value.currentTarget.value);
    };

    useEffect(() => {
        setSelectedField(props.defaultSearchField || props.searchFields[0]);
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        setPlaceholder(props.placeholderOverride || t('misc.searchBy', { searchText: selectedField?.displayName }));
        if (window.innerWidth > 1000) {
            //@ts-ignore
            inputRef?.current?.focus();
        }
        // eslint-disable-next-line
    }, [selectedField]);
    const closeMenu = () => {
        setIsOpen(false);
    };
    const renderSearchOptions = () => {
        return props.searchFields.map((field: ISearchField, index: number) => {
            return (
                <MenuItem
                    className={classes.optionItem}
                    key={field.id}
                    onClick={() => {
                        setSelectedField(field);
                        closeMenu();
                    }}
                >
                    {field.displayName}
                </MenuItem>
            );
        });
    };
    return (
        <div className={classes.root}>
            <div className={classes.searchWrapper} ref={anchorEl}>
                {props.searchFields.length > 1 && (
                    <Button
                        onClick={() => {
                            setIsOpen(true);
                        }}
                        className={classes.optionDropdown}
                    >
                        <span className={classes.optionsDropdownText}>{selectedField?.displayName}</span>
                        <DownChevron />
                    </Button>
                )}
                <input
                    onChange={onChange}
                    type={selectedField?.inputType || 'text'}
                    ref={inputRef}
                    placeholder={placeholder}
                    className={classes.searchInput}
                    value={searchValue}
                />
                <SearchIcon />
            </div>
            {props.searchFields.length > 1 && (
                <Menu
                    anchorEl={anchorEl.current}
                    open={isOpen}
                    onClose={closeMenu}
                    keepMounted
                    TransitionComponent={Fade}
                >
                    {renderSearchOptions()}
                </Menu>
            )}
        </div>
    );
};

SearchComponent.defaultProps = {
    hasBorder: true,
} as Partial<ISearchComponent>;

export default SearchComponent;
